import type {
  BookContent,
  GarmentContent,
  GarmentSection,
  PathSegment,
  SizeChart,
} from '../types/records'
import { tryEvaluate, type EvalContext } from './formula'
import { isPointExpr, tryEvaluatePoint } from './pointExpr'
import { resolveBaseName } from './baseResolver'

export interface ComputedPoint {
  label: string
  x: number
  y: number
  positionDescription: string
  formulaX: string
  formulaY: string
}

export interface ComputedPiece {
  id: string
  italianLabel: string
  points: ComputedPoint[]
  segments: PathSegment[]
}

export interface ComputeError {
  pieceLabel: string
  pointLabel: string
  axis: 'x' | 'y'
  formula: string
  message: string
}

export interface ComputeResult {
  pieces: ComputedPiece[]
  errors: ComputeError[]
  measurementsUsed: Record<string, number>
  derivedUsed: Record<string, number>
}

export interface ComputeInput {
  garment: GarmentContent
  measurements: Record<string, number>
  ease?: number
  // Punti ereditati da un capo base (metodologia Burgo): mappa
  // pezzo→(label→coordinate). Usati come seed SOLO per label che il
  // pezzo del variante non definisce (i punti propri hanno precedenza).
  inheritedPoints?: Record<string, Record<string, { x: number; y: number }>>
  // Fallback globale se il pezzo non trova corrispondenza per nome
  inheritedFallback?: Record<string, { x: number; y: number }>
}

const MAX_PASSES = 8

function normLabel(s: string): string {
  return s.toLowerCase().trim()
}

export function computeGarment(input: ComputeInput): ComputeResult {
  const { garment, measurements, ease = 0, inheritedPoints, inheritedFallback } = input

  // Costruisci derived da CT e altre misure
  const ct = measurements['C.T.'] ?? 0
  const taglia = ct / 2 // donna e uomo: taglia = mezzo torace

  const derived: Record<string, number> = {
    tg: taglia,
    size: taglia,
    taglia: taglia,
    vest: ease,
    // Alias: l'estrazione a volte scrive la parola estesa
    vestibilita: ease,
    'vestibilità': ease,
  }

  // Mappa formule nominate del capo
  const namedFormulas: Record<string, string> = {}
  for (const f of garment.formulas) {
    namedFormulas[f.name] = f.formula
  }

  const errors: ComputeError[] = []
  const computedPieces: ComputedPiece[] = []

  for (const piece of garment.pieces) {
    const pointsByLabel: Record<string, { x: number; y: number }> = {}

    // Seed da capo base: solo per label che questo pezzo NON definisce
    if (inheritedPoints || inheritedFallback) {
      const ownLabels = new Set(piece.points.map(p => p.label))
      const npl = normLabel(piece.italianLabel)
      let pool: Record<string, { x: number; y: number }> | undefined
      if (inheritedPoints) {
        // match esatto del pezzo per nome normalizzato
        for (const [k, v] of Object.entries(inheritedPoints)) {
          if (normLabel(k) === npl) { pool = v; break }
        }
      }
      if (!pool) pool = inheritedFallback
      if (pool) {
        for (const [label, xy] of Object.entries(pool)) {
          if (!ownLabels.has(label)) pointsByLabel[label] = xy
        }
      }
    }

    const remaining = [...piece.points]
    const computed: ComputedPoint[] = []
    const pieceErrors: ComputeError[] = []

    // Iteriamo più volte per risolvere riferimenti tra punti
    let lastRemainingCount = -1
    for (let pass = 0; pass < MAX_PASSES; pass++) {
      if (remaining.length === 0 || remaining.length === lastRemainingCount) break
      lastRemainingCount = remaining.length

      for (let i = 0; i < remaining.length; ) {
        const pt = remaining[i]
        const ctx: EvalContext = {
          measurements,
          points: pointsByLabel,
          namedFormulas,
          derived,
        }

        // Priorità alla costruzione geometrica 2D, se presente e valida
        if (isPointExpr(pt.formulaPoint)) {
          const pRes = tryEvaluatePoint(pt.formulaPoint as string, ctx)
          if (pRes.point !== null) {
            pointsByLabel[pt.label] = { x: pRes.point.x, y: pRes.point.y }
            computed.push({
              label: pt.label,
              x: pRes.point.x,
              y: pRes.point.y,
              positionDescription: pt.positionDescription,
              formulaX: pt.formulaPoint as string,
              formulaY: pt.formulaPoint as string,
            })
            remaining.splice(i, 1)
            continue
          }
          i++
          continue
        }

        const xRes = tryEvaluate(pt.formulaX, ctx)
        const yRes = tryEvaluate(pt.formulaY, ctx)

        if (xRes.value !== null && yRes.value !== null) {
          pointsByLabel[pt.label] = { x: xRes.value, y: yRes.value }
          computed.push({
            label: pt.label,
            x: xRes.value,
            y: yRes.value,
            positionDescription: pt.positionDescription,
            formulaX: pt.formulaX,
            formulaY: pt.formulaY,
          })
          remaining.splice(i, 1)
        } else {
          i++
        }
      }
    }

    // Punti non risolvibili → errori
    for (const pt of remaining) {
      const ctx: EvalContext = {
        measurements,
        points: pointsByLabel,
        namedFormulas,
        derived,
      }

      if (isPointExpr(pt.formulaPoint)) {
        const pRes = tryEvaluatePoint(pt.formulaPoint as string, ctx)
        pieceErrors.push({
          pieceLabel: piece.italianLabel,
          pointLabel: pt.label,
          axis: 'x',
          formula: pt.formulaPoint as string,
          message: pRes.error ?? 'errore sconosciuto',
        })
        continue
      }

      const xRes = tryEvaluate(pt.formulaX, ctx)
      const yRes = tryEvaluate(pt.formulaY, ctx)
      if (xRes.value === null) {
        pieceErrors.push({
          pieceLabel: piece.italianLabel,
          pointLabel: pt.label,
          axis: 'x',
          formula: pt.formulaX,
          message: xRes.error ?? 'errore sconosciuto',
        })
      }
      if (yRes.value === null) {
        pieceErrors.push({
          pieceLabel: piece.italianLabel,
          pointLabel: pt.label,
          axis: 'y',
          formula: pt.formulaY,
          message: yRes.error ?? 'errore sconosciuto',
        })
      }
    }

    errors.push(...pieceErrors)
    computedPieces.push({
      id: piece.id,
      italianLabel: piece.italianLabel,
      points: computed,
      segments: piece.segments,
    })
  }

  return {
    pieces: computedPieces,
    errors,
    measurementsUsed: { ...measurements },
    derivedUsed: { ...derived },
  }
}

// Calcola un capo ereditando i punti dal suo capo base (se applicabile).
// allGarments serve per trovare e calcolare la base.
export function computeWithInheritance(
  garment: GarmentContent,
  allGarments: GarmentContent[],
  measurements: Record<string, number>,
  ease = 0,
): ComputeResult {
  const baseName = resolveBaseName(garment)
  if (!baseName) {
    return computeGarment({ garment, measurements, ease })
  }
  const base = allGarments.find(g => g.name === baseName)
  if (!base) {
    return computeGarment({ garment, measurements, ease })
  }

  const baseResult = computeGarment({ garment: base, measurements, ease })

  const inheritedPoints: Record<string, Record<string, { x: number; y: number }>> = {}
  const inheritedFallback: Record<string, { x: number; y: number }> = {}
  for (const p of baseResult.pieces) {
    const m: Record<string, { x: number; y: number }> = {}
    for (const pt of p.points) {
      m[pt.label] = { x: pt.x, y: pt.y }
      // fallback globale: prima occorrenza vince
      if (!(pt.label in inheritedFallback)) {
        inheritedFallback[pt.label] = { x: pt.x, y: pt.y }
      }
    }
    inheritedPoints[p.italianLabel] = m
  }

  return computeGarment({
    garment,
    measurements,
    ease,
    inheritedPoints,
    inheritedFallback,
  })
}

// Costruisce le misure dal piano taglie del libro per una taglia data.
// Mappa: per ogni riga del size chart, troviamo il codice corrispondente
// dalla measurementsBase via match esatto (case-insensitive) sull'italianName.
export function measurementsFromSize(
  size: string,
  section: GarmentSection,
  book: BookContent,
): Record<string, number> {
  const chart: SizeChart | undefined =
    section === 'donna' ? book.sizesWomen : section === 'uomo' ? book.sizesMen : undefined
  if (!chart) return {}

  const sizeIdx = chart.sizes.indexOf(size)
  if (sizeIdx < 0) return {}

  const nameToCode = new Map<string, string>()
  for (const m of book.measurementsBase) {
    nameToCode.set(m.italianName.toLowerCase().trim(), m.code)
  }

  const result: Record<string, number> = {}
  for (const row of chart.rows) {
    const key = row.italianName.toLowerCase().trim()
    const code = nameToCode.get(key)
    if (code && code !== '?') {
      const v = row.values[sizeIdx]
      if (typeof v === 'number') result[code] = v
    }
  }
  return result
}
